import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
  overline: { es: "Momentos reales", en: "Real moments" },
  title: {
    es: "Eventos y programas realizados",
    en: "Events and programs delivered",
  },
  subtitle: {
    es: "Una muestra de los encuentros, diplomados y congresos donde hemos formado talento MICE en Latinoamérica.",
    en: "A glimpse of the gatherings, diplomas, and congresses where we've trained MICE talent across Latin America.",
  },
  filterAll: { es: "Todos", en: "All" },
  close: { es: "Cerrar", en: "Close" },
  prev: { es: "Anterior", en: "Previous" },
  next: { es: "Siguiente", en: "Next" },
};

// Real event photos. Add new events by appending to this array.
// Each event has: id, name (ES/EN), location, year, and array of photo URLs.
const EVENTS = [
  {
    id: "mpi-2026",
    name: { es: "XVI Congreso MPI México", en: "XVI MPI Mexico Congress" },
    location: { es: "México", en: "Mexico" },
    year: "2026",
    photos: [
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/x0a4kk9q_%23Cobertura%20-%20Las%20conferencias%20simulta%CC%81neas%20del%20%23XVICongreso%20%40mpimexico%20%2C%20se%20esperan%20con%20gran%20inte%20%281%29.jpg",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/p3gj5dx3_IMG-20260720-WA0002.jpg",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/djpf3pii_%23Cobertura%20-%20Las%20conferencias%20simulta%CC%81neas%20del%20%23XVICongreso%20%40mpimexico%20%2C%20se%20esperan%20con%20gran%20inte%20%283%29.jpg",
    ],
  },
  {
    id: "diplomado-irapuato-2025",
    name: { es: "Diplomado en Turismo de Reuniones Irapuato", en: "Meetings Tourism Diploma Irapuato" },
    location: { es: "Irapuato, México", en: "Irapuato, Mexico" },
    year: "2025",
    photos: [
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/1zdm7sjl_IMG-20260109-WA0048.webp",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/tf9ktk2u_IMG-20260109-WA0055.webp",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/3s47rpux_IMG-20260109-WA0062.webp",
    ],
  },
  {
    id: "diplomado-leon-2026",
    name: { es: "Diplomado Industria de Reuniones León", en: "Meetings Industry Diploma León" },
    location: { es: "León, Guanajuato · México", en: "León, Guanajuato · Mexico" },
    year: "2026",
    photos: [
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/g6z6k49m_IMG-20260713-WA0004.jpg",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/ga1xmggh_IMG-20260713-WA0005.jpg",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/m2co1ult_IMG-20260610-WA0043.webp",
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/1witivi8_IMG-20260610-WA0048.webp",
    ],
  },
  {
    id: "pcma-mexico-chapter-2026",
    name: { es: "PCMA Mexico Chapter", en: "PCMA Mexico Chapter" },
    location: { es: "Ciudad de México · México", en: "Mexico City · Mexico" },
    year: "Enero 2026",
    photos: [
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/sf1juwst_motion_photo_7042068062707180599.jpg",
    ],
  },
  // TODO: add more events (Presentación Libro Zacatecas 2026)
];

// Flatten all photos into a single array with event context
const buildPhotoList = (activeEventId) => {
  const events = activeEventId === "all" ? EVENTS : EVENTS.filter((e) => e.id === activeEventId);
  return events.flatMap((event) =>
    event.photos.map((src, idx) => ({
      src,
      event,
      key: `${event.id}-${idx}`,
    }))
  );
};

const EventsGallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [activeEventId, setActiveEventId] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const photos = buildPhotoList(activeEventId);
  const current = lightboxIndex !== null ? photos[lightboxIndex] : null;

  useEffect(() => {
    if (current) {
      document.body.style.overflow = "hidden";
      const onKey = (e) => {
        if (e.key === "Escape") setLightboxIndex(null);
        if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % photos.length);
        if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + photos.length) % photos.length);
      };
      window.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [current, photos.length]);

  return (
    <section
      id="galeria"
      data-testid="events-gallery-section"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#EEF1F4] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <Camera className="w-4 h-4 text-[#2ED3C6]" />
            {t(translations.overline)}
          </span>
          <h2
            data-testid="events-gallery-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-5"
          >
            {t(translations.title)}
          </h2>
          <p className="font-body text-[#0B1F3B]/65 text-base md:text-lg leading-relaxed">
            {t(translations.subtitle)}
          </p>
        </motion.div>

        {/* Filter tabs (visible only if there are 2+ events) */}
        {EVENTS.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap items-center justify-center gap-2 mb-10"
          >
            {[{ id: "all", name: translations.filterAll }, ...EVENTS.map((e) => ({ id: e.id, name: e.name }))].map(
              (filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setActiveEventId(filter.id)}
                  data-testid={`events-filter-${filter.id}`}
                  className={`px-4 py-2 rounded-full font-body text-sm font-semibold transition-all ${
                    activeEventId === filter.id
                      ? "bg-[#0B1F3B] text-white"
                      : "bg-white text-[#0B1F3B]/70 hover:bg-[#0B1F3B]/5"
                  }`}
                >
                  {t(filter.name)}
                </button>
              )
            )}
          </motion.div>
        )}

        {/* Photo grid — masonry-like with variable heights */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
        >
          {photos.map((photo, idx) => (
            <motion.button
              key={photo.key}
              type="button"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.4 + idx * 0.05 }}
              onClick={() => setLightboxIndex(idx)}
              data-testid={`event-photo-${idx}`}
              aria-label={t(photo.event.name)}
              className={`group relative overflow-hidden rounded-xl bg-[#0B1F3B]/10 ${
                idx % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"
              }`}
            >
              <img
                src={photo.src}
                alt={`${t(photo.event.name)} — ${t(photo.event.location)} ${photo.event.year}`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3B]/85 via-[#0B1F3B]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <div className="font-display text-white text-sm font-semibold leading-tight">
                  {t(photo.event.name)}
                </div>
                <div className="font-body text-[#2ED3C6] text-xs mt-0.5">
                  {t(photo.event.location)} · {photo.event.year}
                </div>
              </div>
            </motion.button>
          ))}
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setLightboxIndex(null)}
            data-testid="events-lightbox"
            className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(null);
              }}
              aria-label={t(translations.close)}
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all hover:rotate-90 duration-300 z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((i) => (i - 1 + photos.length) % photos.length);
              }}
              aria-label={t(translations.prev)}
              className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((i) => (i + 1) % photos.length);
              }}
              aria-label={t(translations.next)}
              className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <motion.div
              key={current.key}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
            >
              <img
                src={current.src}
                alt={t(current.event.name)}
                className="max-w-full max-h-[75vh] object-contain rounded-xl"
              />
              <div className="mt-4 text-center">
                <div className="font-display text-white text-lg md:text-xl font-semibold">
                  {t(current.event.name)}
                </div>
                <div className="font-body text-[#2ED3C6] text-sm mt-1">
                  {t(current.event.location)} · {current.event.year} · {lightboxIndex + 1}/{photos.length}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default EventsGallerySection;
